import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { authClient, useSession } from "@/lib/auth-client";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { usePreview } from "./preview-context";

export function WelcomeStep() {
  useDocumentTitle("Welcome");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const { posture } = usePreview();
  const isCos = posture === "chief-of-staff";
  const [name, setName] = useState(session?.user.name?.trim() ?? "");

  const saveName = useMutation({
    mutationFn: async (newName: string) => {
      const { error } = await authClient.updateUser({ name: newName });
      if (error) throw new Error(error.message ?? "Failed to save name");
    },
    onSuccess: (_data, newName) => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      api.onboarding.nameUpdated(newName).catch(() => {});
      // Bounce through the onboarding index so the layout's GatedShell
      // refetches /api/onboarding/state and routes to the next step
      // based on the authoritative server response. Hard-navigating to
      // /onboarding/organization here would force the workspace step on
      // invitees who just supplied their name — the backend now
      // short-circuits them to `complete`.
      navigate({ to: "/onboarding" });
    },
  });

  const trimmed = name.trim();
  const canSubmit = trimmed.length >= 1 && !saveName.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) saveName.mutate(trimmed);
      }}
      className="flex flex-col h-full gap-6"
    >
      {isCos ? (
        <h1 className="text-2xl font-semibold">What should Boo call you?</h1>
      ) : (
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">Nice to meet you 👋</h1>
          <p className="text-sm text-muted-foreground">
            What should we call you? Your Boo will use this when it talks to you.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="display-name">{isCos ? "Name" : "Your name"}</Label>
        <Input
          id="display-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <Button
        type="submit"
        className="w-full mt-auto"
        disabled={!canSubmit}
        loading={saveName.isPending}
      >
        Continue
      </Button>
    </form>
  );
}
